import { createSelector, createSlice } from '@reduxjs/toolkit'
import jwt_decode, { JwtPayload } from 'jwt-decode'

import * as routes from '../config/constants'
import { apiCallBegan } from './api'

interface IsState {
  loading: boolean
  user: JwtPayload | undefined
  app: JwtPayload | undefined
  registerUserId: string | undefined
  token: string | null
  appToken: string | undefined
  isReceivedSms: boolean
  isPasswordChanged: boolean
  isConfirmPhoneNumber: boolean
  error: string | undefined
  message: string | undefined
  userInfo: string | undefined
  userName: string | undefined
  isLogged: boolean
  phoneNumber: string | undefined
  refreshToken: string | undefined
}

const decode = () => {
  const token = localStorage.getItem('token')
  if (token === null) return undefined

  const decodedToken = jwt_decode<JwtPayload>(token)
  if (decodedToken === undefined) return undefined

  return decodedToken
}

const initialState: IsState = {
  loading: false,
  user: decode(),
  app: undefined,
  registerUserId: undefined,
  token: localStorage.getItem('token'),
  appToken: undefined,
  isReceivedSms: false,
  isPasswordChanged: false,
  isConfirmPhoneNumber: false,
  error: undefined,
  message: undefined,
  userInfo: undefined,
  userName: undefined,
  isLogged: false,
  phoneNumber: undefined,
  refreshToken: undefined,
}

const slice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    refreshTokenRequested: (auth) => {
      auth.loading = true
    },
    refreshTokenRequestFailed: (auth, action) => {
      auth.loading = false
    },
    refreshTokenReceived: (auth, action) => {
      auth.loading = false
      auth.refreshToken = action.payload
      localStorage.setItem('token', action.payload.access_token)
      localStorage.setItem('refresh_token', action.payload.refresh_token)

      const decodedToken = jwt_decode<JwtPayload>(action.payload.access_token)
      auth.user = decodedToken

      auth.token = action.payload.access_token
    },
    loginRequested: (auth) => {
      auth.loading = true
    },
    loginRequestFailed: (auth, action) => {
      auth.loading = false

      localStorage.removeItem('token')
      auth.error = 'شماره همراه یا رمز عبور اشتباه می باشد'
    },
    loginReceived: (auth, action) => {
      auth.loading = false
      localStorage.setItem('token', action.payload.access_token)
      localStorage.setItem('refresh_token', action.payload.refresh_token)

      const decodedToken = jwt_decode<JwtPayload>(action.payload.access_token)
      auth.user = decodedToken

      auth.token = action.payload.access_token
      auth.message = 'به ناجی هوشمند خوش آمدید'
    },
    loginGatewayRequested: (auth) => {
      auth.loading = true
    },
    loginGatewayRequestFailed: (auth, action) => {
      auth.loading = false

      localStorage.removeItem('token')
      auth.error = 'شماره همراه یا رمز عبور اشتباه می باشد'
    },
    loginGatewayReceived: (auth, action) => {
      auth.loading = false
      localStorage.setItem('token', action.payload.access_token)
      localStorage.setItem('refresh_token', action.payload.refresh_token)

      const decodedToken = jwt_decode<JwtPayload>(action.payload.access_token)
      auth.user = decodedToken

      auth.token = action.payload.access_token
      auth.message = 'به ناجی هوشمند خوش آمدید'
    },
    appTokenRequested: (auth) => {
      auth.loading = true
    },
    appTokenRequestFailed: (auth) => {
      auth.loading = false
      localStorage.removeItem('app_token')
    },
    appTokenReceived: (auth, action) => {
      auth.loading = false
      localStorage.setItem('app_token', action.payload.access_token)

      const decodedToken = jwt_decode<JwtPayload>(action.payload.access_token)
      auth.app = decodedToken
      auth.appToken = action.payload.access_token
    },
    registerReceived: (auth, action) => {
      auth.loading = false
      auth.registerUserId = action.payload.item1
    },
    registerRequestFailed: (auth, action) => {
      auth.loading = false
      auth.registerUserId = undefined
      auth.error = 'ثبت نام انجام نشد اطلاعات ورودی را بررسی نمایید'
    },
    registerRequested: (auth) => {
      auth.loading = true
    },
    confirmPhoneNumberReceived: (auth, action) => {
      auth.loading = false
      auth.message = 'ثبت نام با موفقیت انجام شد'
    },
    confirmPhoneNumberRequestFailed: (auth, action) => {
      auth.loading = false
      auth.error = 'ثبت نام انجام نشد کد ورودی را بررسی نمایید'
    },
    confirmPhoneNumberRequested: (auth, action) => {
      auth.loading = true
    },
    resetPasswordReceived: (auth, action) => {
      auth.loading = false
      auth.isReceivedSms = true
    },
    resetPasswordRequestFailed: (auth, action) => {
      auth.loading = false
      auth.isReceivedSms = false
      auth.error = 'کاربر گرامی لطفا ثبت نام کنید'
    },
    resetPasswordRequested: (auth, action) => {
      auth.loading = true
    },
    changePasswordReceived: (auth, action) => {
      auth.loading = false
      auth.message = 'رمز عبور با موفقیت تغییر کرد'
    },
    changePasswordRequestFailed: (auth, action) => {
      auth.loading = false
      auth.isPasswordChanged = false
      auth.error = 'تغییرات موفقیت آمیز نبود'
    },
    changePasswordRequested: (auth, action) => {
      auth.loading = true
    },
    updateProfileReceived: (auth, action) => {
      auth.loading = false
      auth.isPasswordChanged = true
      auth.message = 'اطلاعات با موفقیت تغییر کرد'
    },
    updateProfileRequestFailed: (auth, action) => {
      auth.loading = false
      auth.isPasswordChanged = false
      auth.error = 'تغییرات موفقیت آمیز نبود'
    },
    updateProfileRequested: (auth, action) => {
      auth.loading = true
    },
    errorSet: (auth, action) => {
      auth.error = action.payload
    },
    messageSet: (auth, action) => {
      auth.message = action.payload
    },
    getUserInfoReceived: (auth, action) => {
      auth.loading = false
      auth.userInfo = action.payload.properties.city
      auth.userName = action.payload.properties.name
    },
    getUserInfoRequestFailed: (auth, action) => {
      auth.loading = false
    },
    getUserInfoRequested: (auth, action) => {
      auth.loading = true
    },
    loggedSet: (auth, action) => {
      auth.loading = false
      auth.user = undefined
      auth.app = undefined
      auth.registerUserId = undefined
      auth.token = null
      // auth.appToken = undefined
      auth.isReceivedSms = false
      auth.isPasswordChanged = false
      auth.isConfirmPhoneNumber = false
      auth.error = undefined
      auth.message = undefined
      auth.userInfo = undefined
      auth.userName = undefined
      auth.isLogged = false
    },
    loggedUser: (auth, action) => {
      auth.user = action.payload
    },
    tokenUser: (auth, action) => {
      auth.token = action.payload
    },
    setIsReceivedSms: (auth, action) => {
      auth.isReceivedSms = false
    },
    setPhoneNumber: (auth, action) => {
      auth.phoneNumber = action.payload
    },
  },
})
export const phoneNumberSet = (payload: any) => (dispatch: any) => {
  dispatch({
    type: slice.actions.setPhoneNumber.type,
    payload,
  })
}
export const setIsReceivedSmsReset = (dispatch: any) => {
  dispatch({
    type: slice.actions.setIsReceivedSms.type,
  })
}
export const setTokenUser = (payload: any) => (dispatch: any) => {
  dispatch({
    type: slice.actions.tokenUser.type,
    payload,
  })
}
export const setLoggedUser = (payload: any) => (dispatch: any) => {
  dispatch({
    type: slice.actions.loggedUser.type,
    payload,
  })
}

export const setLogged = (dispatch: any) => {
  dispatch({
    type: slice.actions.loggedSet.type,
  })
}
export const setError = (payload: any) => (dispatch: any) => {
  dispatch({
    type: slice.actions.errorSet.type,
    payload,
  })
}
export const setMessage = (payload: any) => (dispatch: any) => {
  dispatch({
    type: slice.actions.messageSet.type,
    payload,
  })
}

export const refreshToken = (data: any) => (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: '/auth/refresh',
      method: 'POST',
      data,
      onStart: slice.actions.refreshTokenRequested.type,
      onError: slice.actions.refreshTokenRequestFailed.type,
      onSuccess: slice.actions.refreshTokenReceived.type,
    }),
  )
}
export const loginGateway = (data: any) => (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: '/auth/login',
      method: 'POST',
      data,
      onStart: slice.actions.loginGatewayRequested.type,
      onError: slice.actions.loginGatewayRequestFailed.type,
      onSuccess: slice.actions.loginGatewayReceived.type,
    }),
  )
}
export const login = (data: any) => (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: '/auth/login',
      method: 'POST',
      data,
      onStart: slice.actions.loginRequested.type,
      onError: slice.actions.loginRequestFailed.type,
      onSuccess: slice.actions.loginReceived.type,
    }),
  )
}
export const register = (data: any) => (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: '/user/register',
      method: 'POST',
      data,
      onStart: slice.actions.registerRequested.type,
      onError: slice.actions.registerRequestFailed.type,
      onSuccess: slice.actions.registerReceived.type,
    }),
  )
}
export const confirmPhoneNumber = (data: any) => (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: '/user/confirmphonenumber',
      method: 'PUT',
      data,
      onStart: slice.actions.confirmPhoneNumberRequested.type,
      onError: slice.actions.confirmPhoneNumberRequestFailed.type,
      onSuccess: slice.actions.confirmPhoneNumberReceived.type,
    }),
  )
}
export const resetPassword = (data: any) => (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: '/user/sendresetpasswordconfirmation',
      method: 'POST',
      data,
      onStart: slice.actions.resetPasswordRequested.type,
      onError: slice.actions.resetPasswordRequestFailed.type,
      onSuccess: slice.actions.resetPasswordReceived.type,
    }),
  )
}
export const changePassword = (data: any) => (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: '/user/resetpassword',
      method: 'POST',
      data,
      onStart: slice.actions.changePasswordRequested.type,
      onError: slice.actions.changePasswordRequestFailed.type,
      onSuccess: slice.actions.changePasswordReceived.type,
    }),
  )
}

export const receiveAppToken = (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: '/auth/apptoken',
      method: 'POST',

      onStart: slice.actions.appTokenRequested.type,
      onError: slice.actions.appTokenRequestFailed.type,
      onSuccess: slice.actions.appTokenReceived.type,
    }),
  )
}
export const updateProfile = (data: any) => (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: '/user/update',
      method: 'PUT',
      data,
      onStart: slice.actions.updateProfileRequested.type,
      onError: slice.actions.updateProfileRequestFailed.type,
      onSuccess: slice.actions.updateProfileReceived.type,
    }),
  )
}
export const getUserInfo = (id: string) => (dispatch: any) => {
  return dispatch(
    apiCallBegan({
      baseURL: routes.BASE_URL,
      url: `/user/${id}`,
      method: 'GET',
      id,
      onStart: slice.actions.getUserInfoRequested.type,
      onError: slice.actions.getUserInfoRequestFailed.type,
      onSuccess: slice.actions.getUserInfoReceived.type,
    }),
  )
}

export const getLoading = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.loading,
)

export const getUser = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.user,
)

export const getApp = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.app,
)

export const getRegisterUserId = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.registerUserId,
)
export const getIsReceivedSms = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.isReceivedSms,
)
export const getIsPasswordChanged = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.isPasswordChanged,
)
export const getisConfirmPhoneNumber = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.isConfirmPhoneNumber,
)
export const getIsUserInfo = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.user,
)
export const getError = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.error,
)
export const getToken = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.token,
)
export const getMessage = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.message,
)
export const getUserCity = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.userInfo,
)
export const getUserName = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.userName,
)
export const getIsLogged = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.isLogged,
)
export const getPhoneNumber = createSelector(
  (state: any) => state.auth,
  (auth: any) => auth.phoneNumber,
)

export default slice.reducer
